import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, Library, Users } from 'lucide-react';
import { Button } from './ui/button';

const WALKTHROUGH_STEPS = [
  {
    id: 'welcome',
    target: null, // No target - centered modal
    title: 'Welcome to Blendonics! 🎧',
    content: "Let's take a quick tour of your new DJ learning platform. This will only take a minute!",
    icon: Sparkles,
    position: 'center'
  },
  {
    id: 'dashboard',
    target: '[data-tour="dashboard"]',
    title: 'Your Dashboard',
    content: 'This is your home base. Track your progress, see your membership status, and get quick access to everything.',
    icon: LayoutDashboard,
    position: 'bottom'
  },
  {
    id: 'content',
    target: '[data-tour="content"]',
    title: 'Content Library',
    content: 'Access video tutorials, PDFs, music resources, and more. New content is added regularly based on your membership tier.',
    icon: Library,
    position: 'bottom'
  },
  {
    id: 'community',
    target: '[data-tour="community"]',
    title: 'DJ Community',
    content: 'Connect with other DJs, share your mixes, get feedback, and grow together. Join rooms based on your interests!',
    icon: Users,
    position: 'bottom'
  },
  {
    id: 'complete',
    target: null,
    title: "You're All Set! 🎉",
    content: "That's the basics! Explore at your own pace. If you need help, check the community or reach out to support.",
    icon: Sparkles,
    position: 'center'
  }
];

const STORAGE_KEY = 'blendonics_walkthrough_completed';

// Tooltip positioning helper
const getTooltipStyle = (targetRect, position, tooltipSize) => {
  if (!targetRect || position === 'center') {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10001
    };
  }

  const padding = 16;
  const arrowSize = 12;
  
  let style = {
    position: 'fixed',
    zIndex: 10001
  };

  switch (position) {
    case 'bottom':
      style.top = targetRect.bottom + arrowSize + padding;
      style.left = targetRect.left + (targetRect.width / 2);
      style.transform = 'translateX(-50%)';
      break;
    case 'top':
      style.bottom = window.innerHeight - targetRect.top + arrowSize + padding;
      style.left = targetRect.left + (targetRect.width / 2);
      style.transform = 'translateX(-50%)';
      break;
    case 'left':
      style.top = targetRect.top + (targetRect.height / 2);
      style.right = window.innerWidth - targetRect.left + arrowSize + padding;
      style.transform = 'translateY(-50%)';
      break;
    case 'right':
      style.top = targetRect.top + (targetRect.height / 2);
      style.left = targetRect.right + arrowSize + padding;
      style.transform = 'translateY(-50%)';
      break;
    default:
      style.top = targetRect.bottom + arrowSize + padding;
      style.left = targetRect.left + (targetRect.width / 2);
      style.transform = 'translateX(-50%)';
  }

  // Keep tooltip within viewport
  if (style.left && typeof style.left === 'number') {
    style.left = Math.max(padding, Math.min(window.innerWidth - 320 - padding, style.left));
  }

  return style;
};

// Spotlight overlay component
const Spotlight = ({ targetRect }) => {
  if (!targetRect) return null;

  const padding = 8;
  
  return (
    <div 
      className="fixed inset-0 z-[10000] pointer-events-none"
      style={{
        background: `radial-gradient(
          ellipse ${targetRect.width + padding * 2}px ${targetRect.height + padding * 2}px at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px,
          transparent 0%,
          transparent 70%,
          rgba(0, 0, 0, 0.85) 70%
        )`
      }}
    />
  );
};

// Main Walkthrough Component
const OnboardingWalkthrough = ({ onComplete, forceShow = false }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = WALKTHROUGH_STEPS[currentStep];

  // Listen for manual trigger event
  useEffect(() => {
    const handleStartWalkthrough = () => {
      setCurrentStep(0);
      setIsActive(true);
    };

    window.addEventListener('startWalkthrough', handleStartWalkthrough);
    return () => window.removeEventListener('startWalkthrough', handleStartWalkthrough);
  }, []);

  // Check if walkthrough should show on initial load
  useEffect(() => {
    if (forceShow) {
      setIsActive(true);
      return;
    }

    // Function to check conditions
    const checkWalkthroughConditions = () => {
      const completed = localStorage.getItem(STORAGE_KEY);
      const token = localStorage.getItem('token');
      const path = window.location.pathname;
      
      // Only show walkthrough for logged-in users who haven't completed it
      // and are on a protected page
      if (!completed && token && (path.includes('/dashboard') || path.includes('/content') || path.includes('/community'))) {
        setIsActive(true);
      }
    };

    // Check immediately and after a delay
    const timer = setTimeout(checkWalkthroughConditions, 1500);
    
    // Also check on route changes
    window.addEventListener('popstate', checkWalkthroughConditions);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('popstate', checkWalkthroughConditions);
    };
  }, [forceShow]);

  // Update target element position
  const updateTargetPosition = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(step.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (isActive) {
      updateTargetPosition();
      window.addEventListener('resize', updateTargetPosition);
      window.addEventListener('scroll', updateTargetPosition);
      return () => {
        window.removeEventListener('resize', updateTargetPosition);
        window.removeEventListener('scroll', updateTargetPosition);
      };
    }
  }, [isActive, currentStep, updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < WALKTHROUGH_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsActive(false);
    if (onComplete) onComplete();
  };

  if (!isActive) return null;

  const Icon = step?.icon || Sparkles;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WALKTHROUGH_STEPS.length - 1;
  const isCentered = step?.position === 'center';

  const tooltipStyle = getTooltipStyle(targetRect, step?.position, { width: 320, height: 200 });

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9999]"
        style={{ background: isCentered ? 'rgba(0, 0, 0, 0.9)' : 'transparent' }}
        onClick={handleSkip}
      />
      
      {/* Spotlight effect for non-centered steps */}
      {!isCentered && <Spotlight targetRect={targetRect} />}

      {/* Tooltip */}
      <div 
        className="w-[320px] rounded-2xl overflow-hidden shadow-2xl"
        style={{
          ...tooltipStyle,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 100%)',
          border: '2px solid rgba(212, 175, 55, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="walkthrough-tooltip"
      >
        {/* Header */}
        <div 
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(212, 175, 55, 0.2)' }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(212, 175, 55, 0.2)' }}
            >
              <Icon size={20} style={{ color: '#D4AF37' }} />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">{step?.title}</h3>
              <p className="text-xs text-gray-400">
                Step {currentStep + 1} of {WALKTHROUGH_STEPS.length}
              </p>
            </div>
          </div>
          <button 
            onClick={handleSkip}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Skip walkthrough"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-gray-300 text-sm leading-relaxed">
            {step?.content}
          </p>
        </div>

        {/* Progress bar */}
        <div className="px-5 pb-2">
          <div className="flex gap-1">
            {WALKTHROUGH_STEPS.map((_, idx) => (
              <div 
                key={idx}
                className="h-1 flex-1 rounded-full transition-all duration-300"
                style={{ 
                  background: idx <= currentStep ? '#D4AF37' : 'rgba(255,255,255,0.1)'
                }}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div 
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(212, 175, 55, 0.2)' }}
        >
          {!isFirstStep ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft size={16} className="mr-1" />
              Back
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-gray-400 hover:text-white"
            >
              Skip Tour
            </Button>
          )}

          <Button
            size="sm"
            onClick={handleNext}
            className="rounded-full px-5"
            style={{ background: '#D4AF37', color: '#1a1a2e', fontWeight: '600' }}
            data-testid="walkthrough-next-btn"
          >
            {isLastStep ? "Let's Go!" : 'Next'}
            {!isLastStep && <ChevronRight size={16} className="ml-1" />}
          </Button>
        </div>
      </div>
    </>,
    document.body
  );
};

// Hook to trigger walkthrough manually
export const useWalkthrough = () => {
  const startWalkthrough = () => {
    localStorage.removeItem(STORAGE_KEY);
    // Dispatch custom event to trigger walkthrough
    window.dispatchEvent(new CustomEvent('startWalkthrough'));
  };

  const resetWalkthrough = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  return { startWalkthrough, resetWalkthrough };
};

export default OnboardingWalkthrough;
